/**
 * HTTP notifications client — Web Push registration against the Functions API.
 */

import type { ApiEnvelope } from '../../domain/types';
import type { AppNotification } from '@zunelo/shared';
import type { HttpClientOptions, INotificationsClient } from './types';
import { createHttpTransport, type HttpTransport } from './httpTransport';
import { parseEnvelope } from './parseEnvelope';

export class HttpNotificationsClient implements INotificationsClient {
  private readonly transport: HttpTransport;

  constructor(options: HttpClientOptions = {}) {
    this.transport = createHttpTransport(options);
  }

  async listNotifications(): Promise<ApiEnvelope<AppNotification[]>> {
    const response = await this.transport.fetch('/api/notifications');
    return parseEnvelope<AppNotification[]>(response, undefined, 'NOTIFICATIONS_HTTP_ERROR');
  }

  async getVapidPublicKey(): Promise<ApiEnvelope<{ key: string }>> {
    const response = await this.transport.fetch('/api/vapid-public-key');
    return parseEnvelope<{ key: string }>(response, undefined, 'NOTIFICATIONS_HTTP_ERROR');
  }

  async registerPushSubscription(
    subscription: PushSubscriptionJSON,
  ): Promise<ApiEnvelope<{ ok: boolean }>> {
    const response = await this.transport.fetch('/api/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
    return parseEnvelope<{ ok: boolean }>(response, undefined, 'NOTIFICATIONS_HTTP_ERROR');
  }

  async sendTestNotification(): Promise<ApiEnvelope<{ ok: boolean; sent?: number }>> {
    const response = await this.transport.fetch('/api/test-notification', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return parseEnvelope<{ ok: boolean; sent?: number }>(
      response,
      undefined,
      'NOTIFICATIONS_HTTP_ERROR',
    );
  }
}
